import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Remembers whether the shopper had already qualified for free shipping.
 *
 * The bar is rendered server-side and re-rendered through the Section Rendering
 * API on every cart change, so "unlocked" alone can't tell us whether this is a
 * fresh crossing of the threshold or just another render of a cart that already
 * qualified. Persisting the last known state keeps the celebration tied to the
 * moment the shopper actually crosses the line.
 */
const STORAGE_KEY = 'free-shipping-bar:unlocked';

/** @type {string | null} */
let fallbackState = null;
let fallbackStateWritten = false;

/** @returns {string | null} */
function readUnlockedState() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return fallbackStateWritten ? fallbackState : null;
  }
}

/** @param {string} value */
function writeUnlockedState(value) {
  fallbackState = value;
  fallbackStateWritten = true;

  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage can be blocked (private windows, cookie settings). The module-level
    // fallback still gives correct behaviour for the lifetime of the page.
  }
}

/**
 * A progress bar showing how close the cart is to the free shipping threshold.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - The progress track the confetti is fired from.
 *
 * @extends {Component<Refs>}
 */
export class FreeShippingBar extends Component {
  static observedAttributes = ['data-unlocked'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-unlocked' || oldValue === newValue || !this.isConnected) return;
    this.#sync();
  }

  #sync() {
    const unlocked = this.dataset.unlocked === 'true';
    const previous = readUnlockedState();

    writeUnlockedState(unlocked ? '1' : '0');

    if (unlocked && previous !== '1') this.#celebrate();
  }

  async #celebrate() {
    if (this.dataset.confetti !== 'true' || prefersReducedMotion()) return;

    const track = this.refs.track ?? this.querySelector('.free-shipping-bar__track');
    if (!(track instanceof HTMLElement)) return;

    const dialog = this.closest('dialog');

    // The threshold is normally crossed while the drawer is still closed or
    // sliding in. The bar measures 0x0 there, so there is no end of the bar to
    // fire from — and no shopper looking at it either. Wait until it is on
    // screen and the drawer has settled, then celebrate.
    const visible = await waitForVisible(track);
    if (!visible) return;

    await settleAnimations(dialog);

    burstConfetti(track, dialog);
  }
}

/** How long to keep waiting for the bar to come on screen before giving up. */
const VISIBILITY_TIMEOUT = 30000;

/**
 * Resolves true once `element` has a non-zero box on screen, false if it never
 * does. The timeout stops a celebration firing long after the moment it belonged
 * to, e.g. if the shopper never opens the drawer at all.
 *
 * @param {HTMLElement} element
 * @returns {Promise<boolean>}
 */
function waitForVisible(element) {
  const isVisible = () => {
    const { width, height } = element.getBoundingClientRect();
    return width > 0 && height > 0;
  };

  if (isVisible()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      clearTimeout(timer);
      resolve(true);
    });

    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(false);
    }, VISIBILITY_TIMEOUT);

    observer.observe(element);
  });
}

/**
 * Waits for any running animations on `element` to finish, so the confetti is
 * measured against the drawer's resting position rather than mid-slide.
 *
 * @param {Element | null} element
 */
async function settleAnimations(element) {
  if (!element) return;
  await Promise.allSettled(element.getAnimations().map((animation) => animation.finished));
}

/**
 * Records the shopper's qualifying state as the page arrives, before any element
 * gets a chance to sync.
 *
 * Without this, a shopper landing with an empty cart has nothing recorded, so the
 * bar's first appearance — the exact moment they qualify — is indistinguishable
 * from a returning shopper's already-qualifying cart, and the celebration gets
 * swallowed. An empty cart is by definition not unlocked, so absence seeds '0'.
 */
function seedUnlockedState() {
  if (readUnlockedState() !== null) return;

  const rendered = document.querySelector('free-shipping-bar');
  writeUnlockedState(rendered?.getAttribute('data-unlocked') === 'true' ? '1' : '0');
}

// Module scripts run after parsing, so the server-rendered bar (if any) is here.
seedUnlockedState();

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

const CONFETTI_COLORS = ['#2fa360', '#7bd68f', '#ffd166', '#ff8fab', '#7ec8ff', '#ffffff'];
const CONFETTI_COUNT = 70;
const GRAVITY = 0.3;
const DRAG = 0.99;
const FADE_START = 900;
const LIFESPAN = 1700;

/**
 * Fires a one-shot confetti burst out of the leading edge of the progress bar.
 *
 * The canvas is fixed to the viewport and appended to the drawer's dialog when
 * there is one — a modal dialog sits in the top layer, so a canvas parked on
 * `document.body` would be painted underneath it no matter its z-index.
 *
 * @param {HTMLElement} track - The progress track to emit from.
 * @param {HTMLElement | null} dialog - The dialog containing the bar, if any.
 */
function burstConfetti(track, dialog) {
  const rect = track.getBoundingClientRect();
  if (!rect.width || !rect.height) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'free-shipping-bar__confetti';
  canvas.setAttribute('aria-hidden', 'true');

  const width = window.innerWidth;
  const height = window.innerHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  canvas.width = Math.floor(width * dpr);
  canvas.height = Math.floor(height * dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const context = canvas.getContext('2d');
  if (!context) return;
  context.scale(dpr, dpr);

  const host = dialog instanceof HTMLDialogElement && dialog.open ? dialog : document.body;
  host.appendChild(canvas);

  const originX = rect.right;
  const originY = rect.top + rect.height / 2;

  const particles = Array.from({ length: CONFETTI_COUNT }, () => {
    // A cone centred straight up, biased outward so the burst reads as spilling
    // off the end of the bar rather than erupting from underneath it.
    const angle = -Math.PI / 2 + (Math.random() - 0.45) * Math.PI * 0.85;
    const speed = 4 + Math.random() * 7;

    return {
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed + 1.4,
      vy: Math.sin(angle) * speed,
      size: 4 + Math.random() * 5,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      rotation: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.32,
      wobble: Math.random() * Math.PI * 2,
    };
  });

  const start = performance.now();
  let frame = 0;

  /** @param {number} now */
  const render = (now) => {
    const elapsed = now - start;
    const alpha = elapsed < FADE_START ? 1 : Math.max(0, 1 - (elapsed - FADE_START) / (LIFESPAN - FADE_START));

    context.clearRect(0, 0, width, height);

    let visible = 0;

    for (const particle of particles) {
      particle.vx *= DRAG;
      particle.vy = particle.vy * DRAG + GRAVITY;
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.rotation += particle.spin;
      particle.wobble += 0.12;

      if (particle.y - particle.size > height) continue;
      visible++;

      context.save();
      context.globalAlpha = alpha;
      context.translate(particle.x, particle.y);
      context.rotate(particle.rotation);
      context.fillStyle = particle.color;
      // Squashing the height on a sine makes each flat ribbon appear to tumble.
      context.fillRect(
        -particle.size / 2,
        -particle.size / 2,
        particle.size,
        particle.size * Math.abs(Math.cos(particle.wobble)) + 1
      );
      context.restore();
    }

    if (visible === 0 || alpha === 0 || elapsed > LIFESPAN) {
      canvas.remove();
      return;
    }

    frame = requestAnimationFrame(render);
  };

  frame = requestAnimationFrame(render);

  // Safety net: if the tab is backgrounded the rAF loop stalls, so make sure the
  // canvas can never outlive the burst by much.
  setTimeout(() => {
    cancelAnimationFrame(frame);
    canvas.remove();
  }, LIFESPAN + 500);
}
